import * as effects from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import * as actions from './actions'
import * as api from './api'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import { ModulePageType, SimpleTaskPayloadType } from './types'
import * as profileActions from '../SignInPage/actions'

function* fetchModuleLessonPage(payload: ModulePageType): SagaIterator {
  try {
    const { id, moduleId, lessonId } = payload
    const courses = yield effects.call(
      api.fetchModuleLessonPage,
      id,
      moduleId,
      lessonId
    )
    yield effects.put(actions.fetchModuleLessonPageSuccess(courses.data))
    yield effects.put(actions.fetchCourseUserState(id))
  } catch (error) {
    console.warn('Cannot fetch lesson page', error)
    yield effects.put(
      actions.fetchModuleLessonPageFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchCourseUserState(payload: { id: string }): SagaIterator {
  try {
    const courses = yield effects.call(api.fetchCourseUserState, payload.id)
    yield effects.put(actions.fetchCourseUserStateSuccess(courses.data))
    yield effects.put(profileActions.setCurrentCourseTitle(courses.data.title))
  } catch (error) {
    console.warn('Cannot fetch course user state', error)
    yield effects.put(
      actions.fetchCourseUserStateFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchSimpleTaskStatus(payload: SimpleTaskPayloadType): SagaIterator {
  try {
    const { simpleTaskId, courseId, moduleId, lessonId } = payload
    const courses = yield effects.call(
      api.fetchSimpleTaskStatus,
      courseId,
      moduleId,
      lessonId,
      simpleTaskId
    )
    yield effects.put(actions.fetchSimpleTaskStatusSuccess(courses.data))
  } catch (error) {
    console.warn('Cannot fetch simple task status', error)
    yield effects.put(
      actions.fetchSimpleTaskStatusFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* joinSimpleTask(payload: SimpleTaskPayloadType): SagaIterator {
  try {
    const { simpleTaskId, courseId, moduleId, lessonId } = payload
    yield effects.call(
      api.joinSimpleTask,
      courseId,
      moduleId,
      lessonId,
      simpleTaskId
    )
    yield effects.put(actions.joinSimpleTaskSuccess())
    yield effects.put(
      actions.fetchModuleLessonPage(courseId, moduleId, lessonId)
    )
  } catch (error) {
    console.warn('Cannot join simple task', error)
    yield effects.put(actions.joinSimpleTaskFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* sendSimpleTask(payload: {
  courseId: string
  moduleId: string
  lessonId: string
  simpleTaskId: string
  field_value?: string
  file?: File
}): SagaIterator {
  try {
    const { courseId, moduleId, lessonId, simpleTaskId, field_value, file } =
      payload
    yield effects.call(
      api.sendSimpleTask,
      courseId,
      moduleId,
      lessonId,
      simpleTaskId,
      field_value,
      file
    )
    yield effects.put(actions.sendSimpleTaskSuccess())
    yield effects.put(
      actions.fetchModuleLessonPage(courseId, moduleId, lessonId)
    )
  } catch (error) {
    console.warn('Cannot send simple task', error)
    yield effects.put(actions.sendSimpleTaskFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* setLessonComplete(payload: ModulePageType): SagaIterator {
  try {
    const { id, moduleId, lessonId } = payload
    const courses = yield effects.call(
      api.setLessonComplete,
      id,
      moduleId,
      lessonId
    )
    yield effects.put(actions.setLessonCompleteSuccess(courses.data))
    yield effects.put(profileActions.fetchNewAchievements())
    yield effects.put(actions.fetchModuleLessonPage(id, moduleId, lessonId))
  } catch (error) {
    console.warn('Cannot set lesson complete', error)
    yield effects.put(
      actions.setLessonCompleteFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchModule(payload: ModulePageType): SagaIterator {
  try {
    const { id, moduleId } = payload
    const courses = yield effects.call(api.fetchModule, id, moduleId)
    yield effects.put(actions.fetchModuleSuccess(courses.data))
  } catch (error) {
    console.warn('Cannot fetch module', error)
    yield effects.put(actions.fetchModuleFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchModuleLessonPage(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchModuleLessonPage.toString(),
    fetchModuleLessonPage
  )
}

function* watchFetchCourseUserState(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchCourseUserState.toString(),
    fetchCourseUserState
  )
}

function* watchFetchSimpleTaskStatus(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchSimpleTaskStatus.toString(),
    fetchSimpleTaskStatus
  )
}

function* watchJoinSimpleTask(): SagaIterator {
  yield effects.takeEvery(actions.joinSimpleTask.toString(), joinSimpleTask)
}

function* watchSendSimpleTask(): SagaIterator {
  yield effects.takeEvery(actions.sendSimpleTask.toString(), sendSimpleTask)
}

function* watchSetLessonComplete(): SagaIterator {
  yield effects.takeEvery(
    actions.setLessonComplete.toString(),
    setLessonComplete
  )
}

function* watchFetchModule(): SagaIterator {
  yield effects.takeEvery(actions.fetchModule.toString(), fetchModule)
}

export default function* watchModuleLessonPage(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchModuleLessonPage),
    effects.fork(watchFetchCourseUserState),
    effects.fork(watchFetchSimpleTaskStatus),
    effects.fork(watchJoinSimpleTask),
    effects.fork(watchSendSimpleTask),
    effects.fork(watchSetLessonComplete),
    effects.fork(watchFetchModule),
  ])
}
